import base, { AxiosInstance } from 'axios';
import { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback, type ReactNode } from 'react';
import { AuthContext, clearStorage } from './Authentication';
import { useRBACOptional } from '@/contexts/RBACContext';
import { apiConfig } from '@/config/apiConfig';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { getActiveTenantHeaders, resolveActiveRequestUserId } from '@/lib/activeTenantScope';
import { ToastContext } from './Toast';
import { usePathname } from 'next/navigation';
import Loader from '@/components/Loader/Loader';

export const VoiceAxiosContext = createContext<AxiosInstance>(base.create());

// False until the initial voice-token check has finished (or a cached token exists). Only
// routes that actually call the Voice service wait on it — see VoiceTokenGate.
export const VoiceAxiosReadyContext = createContext<boolean>(true);

// Every route whose page (or a component it renders) uses VoiceAxiosContext. Matched with
// `includes`, like App.js's publicPaths, so a basePath prefix doesn't break it.
const VOICE_TOKEN_ROUTES = [
  '/voice-',
  '/knowledge-base',
  '/integrations',
  '/conversations',
  '/account/voice-billing',
  '/account/subscription',
];

/**
 * Holds back page content on Voice routes until the voice token is ready. Wraps only the page
 * (inside DashboardLayout), so the sidebar/header and every non-Voice page render immediately
 * instead of the whole app waiting on the Voice service's login round-trip.
 */
export function VoiceTokenGate({ children }: { children: ReactNode }) {
  const ready = useContext(VoiceAxiosReadyContext);
  const path = usePathname();
  const needsVoiceToken = VOICE_TOKEN_ROUTES.some((route) => path?.includes(route));
  if (needsVoiceToken && !ready) return <Loader showDelay />;
  return <>{children}</>;
}

// Cached voice token metadata: which user it belongs to and when it expires (ms epoch).
const VOICE_TOKEN_KEY = 'voice_token';
const VOICE_TOKEN_USER_KEY = 'voice_token_user';
const VOICE_TOKEN_EXPIRES_KEY = 'voice_token_expires_at';
// Refresh this long before the real expiry so an in-flight request never carries a dead token.
const VOICE_TOKEN_REFRESH_SKEW_MS = 60 * 1000;
// Used only when neither the response nor the token itself says when it expires.
const VOICE_TOKEN_FALLBACK_TTL_MS = 30 * 60 * 1000;

/** Expiry (ms epoch) from the auth response's TTL fields, else the JWT `exp` claim, else null. */
function resolveVoiceTokenExpiry(data: any, token: string): number | null {
  const now = Date.now();
  const seconds = Number(data?.expires_in ?? data?.expiresIn ?? data?.ttl);
  if (Number.isFinite(seconds) && seconds > 0) return now + seconds * 1000;
  const at = data?.expires_at ?? data?.expiresAt;
  if (at != null) {
    const ms = typeof at === 'number' ? (at < 1e12 ? at * 1000 : at) : Date.parse(String(at));
    if (Number.isFinite(ms) && ms > now) return ms;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (Number.isFinite(payload?.exp)) return payload.exp * 1000;
  } catch {
    // Not a JWT — fall through.
  }
  return null;
}

/** The cached token if it belongs to `userId` and isn't about to expire, else null. */
function readValidCachedVoiceToken(userId: string | number): { token: string; expiresAt: number } | null {
  try {
    const token = localStorage.getItem(VOICE_TOKEN_KEY);
    const owner = localStorage.getItem(VOICE_TOKEN_USER_KEY);
    const expiresAt = Number(localStorage.getItem(VOICE_TOKEN_EXPIRES_KEY));
    if (!token || owner !== String(userId) || !Number.isFinite(expiresAt)) return null;
    if (expiresAt - VOICE_TOKEN_REFRESH_SKEW_MS <= Date.now()) return null;
    return { token, expiresAt };
  } catch {
    return null;
  }
}

function clearCachedVoiceToken() {
  try {
    localStorage.removeItem(VOICE_TOKEN_KEY);
    localStorage.removeItem(VOICE_TOKEN_USER_KEY);
    localStorage.removeItem(VOICE_TOKEN_EXPIRES_KEY);
  } catch {
    // Storage blocked — nothing cached to clear.
  }
}

function getVoiceInstance(
  voiceToken: string | null,
  auth: any,
  rbac: any,
  setSessionExpiredOpen: (v: boolean) => void,
  onUnauthorized: () => void,
  onRateLimit: (secs: number) => void
) {
  const axios = base.create({
    baseURL: apiConfig.baseUrls.voice,
    withCredentials: true,
  });

  axios.interceptors.request.use((req) => {
    const activeUserId = resolveActiveRequestUserId(auth?.userId);
    // Inject tokens into body for compatibility if needed
    if (req.data && typeof req.data === 'object' && !(req.data instanceof FormData)) {
      if (!req.data.userID && activeUserId) req.data.userID = activeUserId;
      if (req.data.user_id === undefined && activeUserId) req.data.user_id = activeUserId;
      if (auth?.isAuthenticated && !req.data.session_token && auth?.token) {
        req.data.session_token = auth.token;
      }
    }

    // dedicated voice token or fallback to main token
    const token = voiceToken || auth.token;
    if (token) {
      req.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add User and Tenant ID headers
    Object.assign(req.headers, getActiveTenantHeaders());
    if (activeUserId) {
      req.headers['X-User-ID'] = String(activeUserId);
      req.headers['x-user-id'] = String(activeUserId);
    }

    if (rbac?.currentOrgId) {
      req.headers['X-Org-ID'] = String(rbac.currentOrgId);
    }

    if (rbac?.selectedSubAccountId) {
      req.headers['X-Tenant-ID'] = String(rbac.selectedSubAccountId);
    } else if (rbac?.currentAccountId) {
      req.headers['X-Tenant-ID'] = String(rbac.currentAccountId);
    }

    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';

    const requestId = typeof window !== 'undefined' && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : 'req-' + Math.random().toString(36).substring(2, 9);
    req.headers['X-Request-ID'] = requestId;

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          console.warn('Voice API 401 - Clearing token and notifying provider');
          clearCachedVoiceToken();
          onUnauthorized();

          if (!auth?.isAuthenticated) {
            setSessionExpiredOpen(true);
          }
        }

        if (err.response.status === 429) {
          const retryAfterHeader = err.response.headers?.['retry-after'] ?? err.response.headers?.['Retry-After'];
          const secs = retryAfterHeader ? parseInt(String(retryAfterHeader), 10) : 60;
          onRateLimit(Number.isFinite(secs) && secs > 0 ? secs : 60);
        }
      }
      return Promise.reject(err);
    }
  );

  return axios;
}

export default function VoiceAxios({ children }: { children: React.ReactNode }) {
  const authData = useContext(AuthContext);
  const rbac = useRBACOptional();
  const toast = useContext(ToastContext);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [voiceTokenState, setVoiceTokenState] = useState<string | null>(() => {
    if (typeof window === 'undefined' || !authData?.userId) return null;
    return readValidCachedVoiceToken(authData.userId)?.token ?? null;
  });
  // Dedupes concurrent logins, and pins the proactive refresh timer.
  const loginInFlightRef = useRef<Promise<void> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // ── 429 rate-limit state ────────────────────────────────────────────
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);
  const [rateLimitTotal, setRateLimitTotal] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleRateLimit = useCallback((secs: number) => {
    setRateLimitTotal(secs);
    setRateLimitSeconds(secs);
    toast.warning(`Rate limit reached, retry in ${secs}s`, 'Rate Limit');
  }, [toast]);

  const rateLimitActive = rateLimitSeconds > 0;
  useEffect(() => {
    if (!rateLimitActive) {
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      return;
    }
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setRateLimitSeconds(prev => {
        if (prev <= 1) {
          if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLimitActive]);

  // Refs so initVoiceAuth itself doesn't change identity when RBAC finishes loading — it used to
  // list rbac?.currentOrgId / rbac?.isLoading as deps, so the effect below re-ran (and re-logged
  // in) every time either flipped, several times per page load.
  const orgIdRef = useRef(rbac?.currentOrgId);
  orgIdRef.current = rbac?.currentOrgId;
  const rbacWaitingForOrg = Boolean(rbac && rbac.isLoading && !rbac.currentOrgId);

  // Called once per user: a cached token is reused until shortly before it expires (TTL from the
  // exchange response's `expires_in`, else the JWT `exp` claim), then refreshed. `force` (after a 401) always re-exchanges.
  const initVoiceAuth = useCallback(async (force = false) => {
    const userId = authData?.userId;
    if (!userId) {
      // If auth is still loading, wait for it before deciding we don't have a user
      if (authData?.isLoading) return;
      setIsInitializing(false);
      return;
    }

    if (!force) {
      const cached = readValidCachedVoiceToken(userId);
      if (cached) {
        setVoiceTokenState(cached.token);
        setIsInitializing(false);
        scheduleRefresh(cached.expiresAt);
        return;
      }
    }

    // We need currentOrgId for the exchange request's X-Org-ID header; the effect re-runs once it arrives.
    if (rbacWaitingForOrg) return;

    if (loginInFlightRef.current) return loginInFlightRef.current;

    const run = (async () => {
      const orgHeader = orgIdRef.current ? String(orgIdRef.current) : '';

      let tokenToSave: string | null = null;
      let authResponse: any = null;

      // Trades the browser's existing platform session cookie for a voice token, so no credentials
      // live in client code. This one call replaces the old login-or-register + resolve-tenant-id sequence.
      try {
        const exchangeRes = await base.post(`${apiConfig.baseUrls.voice}/api/auth/exchange`, undefined, {
          headers: { 'X-Org-ID': orgHeader },
          withCredentials: true,
        });

        if (exchangeRes.data?.token) {
          tokenToSave = exchangeRes.data.token;
          authResponse = exchangeRes.data;
        }
      } catch (exchangeErr: any) {
        console.error("Voice API token exchange failed", exchangeErr);

        // TEMPORARY: remove once /api/auth/exchange is deployed. Only a 404 (endpoint not there yet)
        // falls back to the legacy login-or-register flow; any other failure is a real error.
        if (exchangeErr?.response?.status === 404) {
          const email = `user_${userId}@boostt.ai`;
          const password = `BoosttVoiceUser123!`;
          const legacyConfig = {
            headers: { 'Content-Type': 'application/json', 'X-Org-ID': orgHeader },
            withCredentials: true,
          };
          try {
            const loginRes = await base.post(`${apiConfig.baseUrls.voice}/api/auth/login`, { username: email, password }, legacyConfig);
            if (loginRes.data?.token) {
              tokenToSave = loginRes.data.token;
              authResponse = loginRes.data;
            }
          } catch {
            try {
              const registerRes = await base.post(`${apiConfig.baseUrls.voice}/api/auth/register`, {
                email,
                password,
                name: `Voice User ${userId}`,
              }, legacyConfig);
              if (registerRes.data?.token) {
                tokenToSave = registerRes.data.token;
                authResponse = registerRes.data;
              }
            } catch (regErr) {
              console.error("Voice API Registration/Login Failed", regErr);
            }
          }

          if (tokenToSave) {
            // Same best-effort tenant_id resolution the legacy flow did after login.
            base.post(`${apiConfig.baseUrls.voice}/api/workspaces/current/resolve-tenant-id`, {}, {
              headers: { Authorization: `Bearer ${tokenToSave}` },
              withCredentials: true,
            }).catch(() => { /* best-effort — usage tracking, not auth-critical */ });
          }
        }
      }

      if (tokenToSave) {
        const expiresAt = resolveVoiceTokenExpiry(authResponse, tokenToSave) ?? Date.now() + VOICE_TOKEN_FALLBACK_TTL_MS;
        try {
          localStorage.setItem(VOICE_TOKEN_KEY, tokenToSave);
          localStorage.setItem(VOICE_TOKEN_USER_KEY, String(userId));
          localStorage.setItem(VOICE_TOKEN_EXPIRES_KEY, String(expiresAt));
        } catch {
          // Storage blocked — token still lives in state for this session.
        }
        setVoiceTokenState(tokenToSave);
        scheduleRefresh(expiresAt);
      }
    })();

    loginInFlightRef.current = run;
    try {
      await run;
    } catch (err) {
      console.error("Failed to initialize Voice API auth", err);
    } finally {
      loginInFlightRef.current = null;
      setIsInitializing(false);
    }
    // scheduleRefresh is a stable function declaration below (only touches refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.userId, authData?.isLoading, rbacWaitingForOrg]);

  // Proactively re-exchange shortly before the token expires, so Voice calls never hit a 401 for
  // an expired token mid-session.
  function scheduleRefresh(expiresAt: number) {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max(expiresAt - VOICE_TOKEN_REFRESH_SKEW_MS - Date.now(), 0);
    // setTimeout overflows past ~24.8 days; a token that long-lived is re-checked on next load.
    if (delay > 2 ** 31 - 1) return;
    refreshTimerRef.current = setTimeout(() => {
      initVoiceAuthRef.current?.(true);
    }, delay);
  }
  const initVoiceAuthRef = useRef<typeof initVoiceAuth | null>(null);
  initVoiceAuthRef.current = initVoiceAuth;

  useEffect(() => {
    initVoiceAuth();
  }, [initVoiceAuth]);

  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  const handleUnauthorized = useCallback(() => {
    // Attempt to re-fetch token once on next render
    initVoiceAuth(true);
  }, [initVoiceAuth]);

  const client = useMemo(
    () => getVoiceInstance(voiceTokenState, authData || {}, rbac || {}, setSessionExpiredOpen, handleUnauthorized, handleRateLimit),
    // Depend only on the primitive fields getVoiceInstance actually reads off authData/rbac —
    // not the objects themselves. Both contexts hand out a new object identity on every
    // refresh (e.g. RBACContext's tab-visibility permission refetch), which would otherwise
    // recreate the axios client and cascade into refetches of every page keyed on voiceAxios.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      voiceTokenState,
      authData?.userId,
      authData?.isAuthenticated,
      authData?.token,
      rbac?.currentOrgId,
      rbac?.selectedSubAccountId,
      rbac?.currentAccountId,
      handleUnauthorized,
      handleRateLimit,
    ]
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    clearCachedVoiceToken();
    location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
  };

  // Previously `return null` here while initializing, which blanked the ENTIRE app (this
  // provider wraps DashboardLayout) until the Voice login round-trip finished — on every fresh
  // login, since logout clears voice_token. Now only VoiceTokenGate'd Voice routes wait.
  const voiceReady = !isInitializing || Boolean(voiceTokenState);

  return (
    <>
      <VoiceAxiosReadyContext.Provider value={voiceReady}>
        <VoiceAxiosContext.Provider value={client}>
          {children}
        </VoiceAxiosContext.Provider>
      </VoiceAxiosReadyContext.Provider>

      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">Session Expired</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">Your voice session has expired. Please log in again to continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* 429 Rate-Limit Dialog */}
      <Dialog
        open={rateLimitSeconds > 0}
        onClose={() => setRateLimitSeconds(0)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'error.main' }} />
            <Typography variant="h6" component="div">Rate limit reached</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Too many requests were sent. The API is temporarily throttling your session.
          </Alert>
          <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
            Auto-resuming in <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{rateLimitSeconds}s</Box>. You can dismiss this and retry manually.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={rateLimitTotal > 0 ? (rateLimitSeconds / rateLimitTotal) * 100 : 100}
            sx={{ borderRadius: 4, height: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateLimitSeconds(0)} variant="outlined" size="small">
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
